import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import Accordion from '../../../../components/Accordion/Accordion';
import Button from '../../../../components/Button/Button';
import { useAppDispatch, useAppSelector } from '../../../../reduxService/hooks';
import Layout from '../../../components/Layout/Layout';
import isAdminUser from '../../../hoc/isAdminUser';
import { endpoint } from '../../../pages';
import './BackofficeTemplate.scss';
import { IBackofficeTemplateProps } from './IBackofficeTemplate';
import React from 'react';
import { cleanProject, fetchTemplateById } from '../../store/project/projectSlice';
import * as BeMaccaferriStoreHelper from '../../../api/store/BeMaccaferriStoreHelper';
import { IMacra2TemplateRequest, ITemplateRequest } from '../../../api/IApiRequest';
import { ELanguages, TLanguages, languages } from '../../../localization';
import { Menu, MenuItem } from '@mui/material';
import { selectProject } from '../../selector/conversion';
import Spinner from '../../../../components/Spinner/Spinner';
import Input from '../../../../components/Input/Input';
import CrossSection from '../../components/graphicalResults/crossSection/CrossSection';
import InputForm from '../../components/InputForm/InputForm';
import { addIdToFile, projectMacra2ToTemplate } from '../../../utils/utils';
import { selectProjectInfo } from '../../../store/projectInfo/projectInfoSlice';
import { EProjectType } from '../../../enum/project';
import { toastSuccess } from '../../../../components/Alert/Alert';

const BackofficeTemplate = (props: IBackofficeTemplateProps) => {
    const { t, i18n } = useTranslation();
    const { id, type } = useParams();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const project = useAppSelector(selectProject);
    const projectInfo = useAppSelector(selectProjectInfo);
    const defaultTemplate: any = {
        infos: [{ languageCodeISO: 'en', name: '' }],
        isDeleted: false,
    };
    const [canShow, setCanShow] = React.useState<boolean>(false);
    const [template, setTemplate] = React.useState<ITemplateRequest>(defaultTemplate);
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const [loading, setLoading] = React.useState<boolean>(false);
    const languagesAvailable = Object.keys(ELanguages).filter(l => !template.infos.find((el: any) => el.languageCodeISO === l));
    const inputImage = React.useRef<HTMLInputElement | null>(null);
    const [imgUrl, setImgUrl] = React.useState<string>('');

    const handleClickMenu = (e: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(e.currentTarget);
    };
    const handleCloseMenu = () => {
        setAnchorEl(null);
    };

    const addLanguage = (l: string) => {
        setTemplate({
            ...template,
            infos: [...template.infos, { languageCodeISO: l, name: '' }],
        });
    };
    const handleCancel = () => {
        navigate(endpoint.backoffice);
    };
    const open = Boolean(anchorEl);

    const handleSave = async () => {
        if (!!project.data?.geometry) {
            let templateCompleted: IMacra2TemplateRequest = projectMacra2ToTemplate(project.data, template.infos);
            if (!!id) {
                await BeMaccaferriStoreHelper.updateGCSTemplateAsync({
                    ...template,
                    ...templateCompleted,
                    image: template.image,
                    id: parseInt(id),
                    type: projectInfo.data?.type,
                    imageUrl: imgUrl,
                    hydraulicData: project.data.hydraulicData!,
                });
            } else {
                await BeMaccaferriStoreHelper.addGCSTemplateAsync({
                    ...template,
                    ...templateCompleted,
                    image: template.image,
                    type: projectInfo.data?.type,
                    hydraulicData: project.data.hydraulicData!,
                });
            }
        }

        dispatch(cleanProject());
        navigate(endpoint.backoffice);
        toastSuccess(t('header_projects_saved'));
    };

    React.useEffect(() => {
        if (!project.initialState && !project.pending && !projectInfo.initialState && !!projectInfo.data?.type) {
            const timer = setTimeout(() => {
                setCanShow(true);
            }, 1000);
            return () => clearTimeout(timer);
        }
    }, [project, projectInfo]);

    React.useEffect(() => {
        const fetchTemplate = async () => {
            setLoading(true);
            if (!!type) {
                const templateTyped = await BeMaccaferriStoreHelper.getTemplatesByTypeAsync(EProjectType.Macra2 | (parseInt(type) as EProjectType), 1, 1);
                dispatch(fetchTemplateById({ id: templateTyped.data.data[0].id, fromBo: true }));
                const res: any = await BeMaccaferriStoreHelper.getTemplateGCSByIdAsync(templateTyped.data.data[0].id);
                res.data.infos = defaultTemplate.infos;
                res.data.name = '';
                setImgUrl('');
                setTemplate(res.data);
            } else {
                if (!!id) {
                    dispatch(fetchTemplateById({ id: parseInt(id), fromBo: true }));
                    const templateWithLanguages: any = await BeMaccaferriStoreHelper.getGCSWithAllLanguagesAsync(parseInt(id));
                    setImgUrl(templateWithLanguages.data.imageUrl || '');
                    setTemplate(templateWithLanguages.data);
                } else {
                    const templates = await BeMaccaferriStoreHelper.getGCSPreviewTemplatesAsync(1, 1);
                    dispatch(fetchTemplateById({ id: templates.data.data[0].id, fromBo: true }));
                    const res: any = await BeMaccaferriStoreHelper.getTemplateGCSByIdAsync(templates.data.data[0].id);
                    res.data.infos = defaultTemplate.infos;
                    res.data.name = '';
                    setImgUrl('');
                    setTemplate(res.data);
                }
            }
            setLoading(false);
        };
        fetchTemplate();
    }, [id, type]);

    const handleInputName = (event: React.ChangeEvent<HTMLInputElement>) => {
        const target = event.target;
        const name = target.name;
        const newInfos = template.infos.map((info: any) => {
            if (info.languageCodeISO === name) info.name = target.value;
            return info;
        });
        setTemplate({
            ...template,
            infos: newInfos,
        });
    };

    React.useEffect(() => {
        if (projectInfo.data && projectInfo.data.imgUrl) setImgUrl(projectInfo.data?.imgUrl);
    }, [projectInfo.data]);

    const handleChangeUploadLocalImage = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e?.target?.files && e?.target?.files[0]) {
            let original = e.target.files[0];
            let filename = addIdToFile(original.name);
            const img = new File([original], filename, {
                type: original.type,
                lastModified: original.lastModified,
            });

            setImgUrl(URL.createObjectURL(img));
            setTemplate({
                ...template,
                image: img,
            });
        }
    };
    const handleUploadLocalImage = () => {
        inputImage.current?.click();
    };

    return (
        <Layout>
            <div className='d-flex p-2 gap-2 h-85'>
                <div className='w-25 d-flex flex-column gap-2 overflow-auto'>
                    <label className='py-3'>{t('backoffice_general_info')}</label>
                    {template.infos.map((info: any) => {
                        let x = info.languageCodeISO as unknown as TLanguages;
                        const language = languages[x];
                        return (
                            <div key={info.languageCodeISO}>
                                {!!language && (
                                    <Accordion title={language}>
                                        <Input type='text' title='Name' name={info.languageCodeISO} className='d-flex flex-column' value={info?.name} onChange={handleInputName} />
                                        <Button
                                            className='delete-btn-wrapper'
                                            type='button'
                                            onClick={() => setTemplate((value: any) => ({ ...value, infos: value.infos.filter((el: any) => el.languageCodeISO !== x) }))}>
                                            <i className='fa-solid fa-trash icon-delete' />
                                        </Button>
                                    </Accordion>
                                )}
                            </div>
                        );
                    })}

                    <Button className='d-flex justify-content-end pt-2' classNameBtn='btn-color-inverted' onClick={handleClickMenu}>
                        <i className='fa-solid fa-plus' />
                    </Button>
                    <Menu
                        id='basic-menu-bo-template'
                        anchorEl={anchorEl}
                        open={open}
                        onClose={handleCloseMenu}
                        MenuListProps={{
                            'aria-labelledby': 'basic-button',
                        }}>
                        <div>
                            {languagesAvailable.map(l => (
                                <MenuItem key={l} onClick={() => addLanguage(l)}>
                                    {languages[l as TLanguages]}
                                </MenuItem>
                            ))}
                        </div>
                    </Menu>
                </div>
                {loading ? (
                    <Spinner></Spinner>
                ) : (
                    <>
                        <div className='w-25 d-flex flex-column gap-2 overflow-auto'>
                            <label className='py-3'>{t('backoffice_data')}</label>
                            {!project.initialState && !project.pending && !projectInfo.initialState && !!projectInfo.data?.type ? (
                                <InputForm>
                                    <Accordion title={t('backoffice_template_picture')}>
                                        <div className='d-flex justify-content-around pictureBox'>
                                            <Button classNameBtn='btn-color-inverted' onClick={handleUploadLocalImage}>
                                                <i className='fa-solid fa-arrow-up-from-bracket' />
                                                <input className='d-none' type='file' ref={inputImage} onChange={handleChangeUploadLocalImage} />
                                            </Button>
                                            {imgUrl && <img src={imgUrl} alt={t('backoffice_template_picture_alt') as string} className='w-75 h-75' />}
                                        </div>
                                    </Accordion>
                                </InputForm>
                            ) : (
                                <Spinner />
                            )}
                        </div>
                        <div className='w-50'>
                            <label className='py-3'>{t('backoffice_board')}</label>
                            {canShow ? <CrossSection fromBackOffice /> : <Spinner />}
                            <div className='d-flex justify-content-end gap-3 mt-3'>
                                <Button classNameBtn='btn-color-inverted' onClick={handleCancel}>
                                    {t('cancel_button')}
                                </Button>
                                <Button onClick={handleSave}>{t('header_save_button')}</Button>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </Layout>
    );
};

export default isAdminUser(BackofficeTemplate);
